import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { fadeInUp } from '../utils/cn';

const HazardousDivisionCompany = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="section-padding solid-bg text-white relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('/divisions/Hazardous/industrial-complex-at-night-2023-11-27-05-34-39-utc.jpg')`
          }}
        />
        
        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-accent rounded-full opacity-30 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        <div className="container-max relative z-10">
          <motion.div
            className="text-center"
            {...fadeInUp}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Our Partner Companies
            </h1>
            <p className="text-xl md:text-xl mb-8 leading-relaxed max-w-4xl mx-auto">
              Partnering with industry leaders to deliver world-class hazardous area solutions. 
              We collaborate with specialized companies that share our commitment to safety, 
              innovation, and excellence in hazardous environments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" variant="secondary">
                  Get Quote
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </Link>
              <Link to="/divisions/hazardous">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-charcoal">
                  Back to Hazardous Division
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="section-padding bg-white text-black">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-global-green">
              Companies Global Technology Represent
            </h2>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Partnering with industry leaders to deliver world-class hazardous area solutions.
            </p>
          </motion.div>

          <motion.div
            className="bg-gradient-to-r from-charcoal to-slate rounded-2xl p-8 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4 flex items-center text-global-green">
                  <Shield className="w-8 h-8 mr-3 text-global-green" />
                  Ex-Machinery
                </h3>
                <h4 className="text-xl font-semibold mb-4 text-global-green">
                  Ensuring Explosion Safety
                </h4>
                <h5 className="text-lg font-medium mb-4 text-black">
                  Specialist in ATEX Equipment - Manufacturer of Explosion Proof Air Conditioners, 
                  Heaters, WIFI Systems, and Custom Machinery.
                </h5>
                <p className="text-black mb-6 leading-relaxed">
                  Ex-Machinery is specialized in manufacturing industrial, certified explosion proof HVAC equipment 
                  and Wi-Fi systems. Ex-Machinery has provided systems of all sizes throughout the world for refineries, 
                  chemical plants, offshore oil platforms, drill ships, FPSO vessels, and other facilities. Ex-Machinery 
                  provides solutions for the petrochemical market, combining safety with convenience. From explosion proof 
                  air conditioners to complete air filtration and heating ventilation systems, we have the expertise to 
                  provide a custom air solution for your needs.
                </p>
                <p className="text-black mb-6 leading-relaxed">
                  With full documentation support and FAT testing and certification, our products are the best choice for 
                  your climatic control needs. Our explosion proof, industrial HVAC solutions are used by some of the leaders 
                  in the petrochemical industry. We offer our products, service and support to organizations around the globe 
                  so you can focus on production. Our explosion proof HVAC systems give you peace of mind and superior safety 
                  through quality of engineering.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center text-black">
                    <Shield className="w-5 h-5 text-global-green mr-2" />
                    <span className="text-sm">20+ Years Experience</span>
                  </div>
                  <div className="flex items-center text-black">
                    <Shield className="w-5 h-5 text-global-green mr-2" />
                    <span className="text-sm">ATEX Certified</span>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-black">Product Range</h4>
                    <ul className="space-y-2 text-black">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Explosion Proof Air Conditioners
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Explosion Proof Heaters
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Explosion Proof WIFI Systems
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Custom ATEX Machinery
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-black">Applications</h4>
                    <ul className="space-y-2 text-black">
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Refineries & Chemical Plants
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Offshore Oil Platforms
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Drill Ships & FPSO Vessels
                      </li>
                      <li className="flex items-center text-black">
                        <CheckCircle className="w-4 h-4 text-global-green mr-2 flex-shrink-0" />
                        Petrochemical Facilities
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="border-t border-black/20 pt-6">
                  <p className="text-sm leading-relaxed opacity-90 text-black">
                    Ex-Machinery has over 20 years of experience in the field of ATEX and explosion safety. In collaboration 
                    with OEM's, Ex-Machinery modifies state-of-the-art equipment and machinery to be ATEX compliant. Through 
                    this philosophy Ex-Machinery guarantees explosion safety and up-to-date quality, for every product.
                  </p>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-lg font-semibold mb-2 text-black">Industry Markets</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Refineries</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Chemical Plants</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Oil Platforms</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Drill Ships</span> 
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">FPSO Vessels</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">HVAC Systems</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Air Filtration</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Heating Systems</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Custom Solutions</span>
                  </div>
                </div>

                <div className="text-center pt-4">
                  <p className="text-sm font-medium text-black">
                    Ex-Machinery guarantees explosion safety and superior quality through engineering excellence
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HazardousDivisionCompany;